/**
 * achievements.js — pure derivation of badges/certificates from progress data.
 *
 * Data sources (no new backend):
 *   allProgress: { [subjectKey]: { [chapterId]: { lectures, dpps, tests } } }
 *   chapters from SUBJECT_CHAPTERS (tutorialCatalogue.js)
 *
 * Award tiers:
 *   chapter badge     — every item (lecture/dpp/test) in a chapter done
 *   subject certificate — every chapter in a subject complete
 *   program trophy    — every subject of a program complete
 */

import { SUBJECT_CHAPTERS, PROGRAM_SUBJECT_KEYS } from '../config/tutorialCatalogue';

// Subject display metadata + badge color theme (per-subject consistent color)
export const SUBJECT_META = {
  'iitjee-physics':      { label: 'IIT-JEE Physics',      hue: '#6366f1', hue2: '#4338ca', emoji: '🧲' },
  'iitjee-chemistry':    { label: 'IIT-JEE Chemistry',    hue: '#f59e0b', hue2: '#b45309', emoji: '⚗️' },
  'iitjee-mathematics':  { label: 'IIT-JEE Mathematics',  hue: '#0ea5e9', hue2: '#0369a1', emoji: '📐' },
  'neet-biology':        { label: 'NEET Biology',         hue: '#22c55e', hue2: '#15803d', emoji: '🧬' },
  'class12-physics':     { label: 'Class 12 Physics',     hue: '#8b5cf6', hue2: '#6d28d9', emoji: '🧲' },
  'class12-chemistry':   { label: 'Class 12 Chemistry',   hue: '#f59e0b', hue2: '#b45309', emoji: '⚗️' },
  'class12-mathematics': { label: 'Class 12 Mathematics', hue: '#0ea5e9', hue2: '#0369a1', emoji: '📐' },
  'class12-biology':     { label: 'Class 12 Biology',     hue: '#22c55e', hue2: '#15803d', emoji: '🧬' },
  'class11-physics':     { label: 'Class 11 Physics',     hue: '#8b5cf6', hue2: '#6d28d9', emoji: '🧲' },
  'class11-chemistry':   { label: 'Class 11 Chemistry',   hue: '#f59e0b', hue2: '#b45309', emoji: '⚗️' },
  'class11-mathematics': { label: 'Class 11 Mathematics', hue: '#0ea5e9', hue2: '#0369a1', emoji: '📐' },
  'class11-biology':     { label: 'Class 11 Biology',     hue: '#22c55e', hue2: '#15803d', emoji: '🧬' },
  'class10-mathematics': { label: 'Class 10 Mathematics', hue: '#0ea5e9', hue2: '#0369a1', emoji: '📐' },
  'class10-science':     { label: 'Class 10 Science',     hue: '#10b981', hue2: '#047857', emoji: '🔬' },
  'class10-english':     { label: 'Class 10 English',     hue: '#ec4899', hue2: '#be185d', emoji: '📖' },
  'class9-mathematics':  { label: 'Class 9 Mathematics',  hue: '#0ea5e9', hue2: '#0369a1', emoji: '📐' },
  'class9-science':      { label: 'Class 9 Science',      hue: '#10b981', hue2: '#047857', emoji: '🔬' },
  'class9-english':      { label: 'Class 9 English',      hue: '#ec4899', hue2: '#be185d', emoji: '📖' },
  'class8-mathematics':  { label: 'Class 8 Mathematics',  hue: '#0ea5e9', hue2: '#0369a1', emoji: '📐' },
  'class8-science':      { label: 'Class 8 Science',      hue: '#10b981', hue2: '#047857', emoji: '🔬' },
  'class8-english':      { label: 'Class 8 English',      hue: '#ec4899', hue2: '#be185d', emoji: '📖' },
  'class7-mathematics':  { label: 'Class 7 Mathematics',  hue: '#0ea5e9', hue2: '#0369a1', emoji: '📐' },
  'class7-science':      { label: 'Class 7 Science',      hue: '#10b981', hue2: '#047857', emoji: '🔬' },
  'class7-english':      { label: 'Class 7 English',      hue: '#ec4899', hue2: '#be185d', emoji: '📖' },
  'class6-mathematics':  { label: 'Class 6 Mathematics',  hue: '#0ea5e9', hue2: '#0369a1', emoji: '📐' },
  'class6-science':      { label: 'Class 6 Science',      hue: '#10b981', hue2: '#047857', emoji: '🔬' },
  'class6-english':      { label: 'Class 6 English',      hue: '#ec4899', hue2: '#be185d', emoji: '📖' },
};

export const PROGRAM_META = {
  jee:     { label: 'IIT-JEE Program',  hue: '#4f46e5', hue2: '#7c3aed', emoji: '🏆' },
  neet:    { label: 'NEET Program',     hue: '#db2777', hue2: '#9333ea', emoji: '🏆' },
  class12: { label: 'Class 12 Program', hue: '#0891b2', hue2: '#059669', emoji: '🏆' },
  class11: { label: 'Class 11 Program', hue: '#0ea5e9', hue2: '#6366f1', emoji: '🏆' },
  class10: { label: 'Class 10 Program', hue: '#f59e0b', hue2: '#ef4444', emoji: '🏆' },
  class9:  { label: 'Class 9 Program',  hue: '#f97316', hue2: '#eab308', emoji: '🏆' },
  class8:  { label: 'Class 8 Program',  hue: '#06b6d4', hue2: '#6366f1', emoji: '🏆' },
  class7:  { label: 'Class 7 Program',  hue: '#8b5cf6', hue2: '#06b6d4', emoji: '🏆' },
  class6:  { label: 'Class 6 Program',  hue: '#10b981', hue2: '#3b82f6', emoji: '🏆' },
};

export function subjectMeta(subjectKey) {
  return SUBJECT_META[subjectKey] || { label: subjectKey, hue: '#64748b', hue2: '#334155', emoji: '🎖️' };
}

// Subjects whose content is shared between JEE and NEET (same chapters/lectures).
const JEE_NEET_SHARED = new Set(['iitjee-physics', 'iitjee-chemistry']);

/**
 * Resolve the display label for a subject, personalised by what the student is
 * preparing for. JEE/NEET share physics & chemistry content, so on a NEET
 * aspirant's certificate we show "NEET Physics" instead of "IIT-JEE Physics".
 *
 * preparingFor values seen in the app: 'JEE', 'NEET', 'Board Exam', 'School',
 * 'Olympiad', or free-text like 'JEE 2027'.
 */
export function resolveSubjectLabel(subjectKey, preparingFor) {
  const base = subjectMeta(subjectKey);
  if (!JEE_NEET_SHARED.has(subjectKey)) return base.label;
  const prep = (preparingFor || '').toLowerCase();
  if (prep.includes('neet')) return base.label.replace('IIT-JEE', 'NEET');
  return base.label; // default to IIT-JEE
}

/** Human-friendly chapter title: "Chapter 2: Kinematics" → from catalogue or fallback. */
export function chapterTitle(subjectKey, chapterId) {
  const ch = (SUBJECT_CHAPTERS[subjectKey] || []).find(c => c.id === chapterId);
  if (!ch) return chapterId;
  // catalogue chapters have no title — derive from id: "chapter-2-kinematics" → "Chapter 2 · Kinematics"
  const m = /^chapter-(\d+)-(.+)$/.exec(chapterId);
  if (m) {
    const name = m[2].split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
    return `Chapter ${m[1]} · ${name}`;
  }
  return chapterId;
}

/** Per-chapter completion for one subject. */
export function chapterStats(subjectKey, chapterId, allProgress) {
  const ch = (SUBJECT_CHAPTERS[subjectKey] || []).find(c => c.id === chapterId);
  if (!ch) return { total: 0, done: 0, pct: 0, completed: false, remaining: 0 };
  const cp = allProgress?.[subjectKey]?.[chapterId] || {};
  const lecs = cp.lectures || {};
  const dpps = cp.dpps || {};
  const tests = cp.tests || {};
  let total = 0, done = 0;
  let totalLecs = 0, doneLecs = 0, totalDpps = 0, doneDpps = 0, totalTests = 0, doneTests = 0;
  (ch.lectures || []).forEach(l => { total++; totalLecs++; if (lecs[l.id]?.done) { done++; doneLecs++; } });
  (ch.dpps || []).forEach(d => { total++; totalDpps++; if (dpps[d.fileName]?.done) { done++; doneDpps++; } });
  (ch.tests || []).forEach(t => { total++; totalTests++; if (tests[t.testId]?.completed) { done++; doneTests++; } });
  return {
    total, done,
    pct: total > 0 ? Math.round((done / total) * 100) : 0,
    completed: total > 0 && done === total,
    remaining: total - done,
    // per-type remaining for the nudge pop-up
    lecsRemaining: totalLecs - doneLecs,
    dppsRemaining: totalDpps - doneDpps,
    testsRemaining: totalTests - doneTests,
    totalLecs, totalDpps, totalTests,
  };
}

/** All earned chapter badges: [{ subjectKey, chapterId, title, meta }] */
export function earnedChapterBadges(allProgress) {
  const out = [];
  Object.keys(SUBJECT_CHAPTERS).forEach(subjectKey => {
    (SUBJECT_CHAPTERS[subjectKey] || []).forEach(ch => {
      const s = chapterStats(subjectKey, ch.id, allProgress);
      if (s.completed) {
        out.push({
          type: 'chapter',
          subjectKey,
          chapterId: ch.id,
          title: chapterTitle(subjectKey, ch.id),
          meta: subjectMeta(subjectKey),
        });
      }
    });
  });
  return out;
}

/** Earned subject certificates: [{ subjectKey, meta, badgeCount }] */
export function earnedSubjectCertificates(allProgress) {
  const out = [];
  Object.keys(SUBJECT_CHAPTERS).forEach(subjectKey => {
    const chapters = SUBJECT_CHAPTERS[subjectKey] || [];
    const completable = chapters.filter(ch => chapterStats(subjectKey, ch.id, allProgress).total > 0);
    if (completable.length === 0) return;
    const allDone = completable.every(ch => chapterStats(subjectKey, ch.id, allProgress).completed);
    if (allDone) {
      out.push({
        type: 'subject',
        subjectKey,
        meta: subjectMeta(subjectKey),
        badgeCount: completable.length,
      });
    }
  });
  return out;
}

/** Earned program trophies: [{ programId, meta }] */
export function earnedProgramTrophies(allProgress, enrolledProgramIds) {
  const out = [];
  (enrolledProgramIds || []).forEach(programId => {
    const keys = PROGRAM_SUBJECT_KEYS[programId] || [];
    const certs = earnedSubjectCertificates(allProgress);
    const certKeys = new Set(certs.map(c => c.subjectKey));
    const completableKeys = keys.filter(k => (SUBJECT_CHAPTERS[k] || []).some(ch => chapterStats(k, ch.id, allProgress).total > 0));
    if (completableKeys.length > 0 && completableKeys.every(k => certKeys.has(k))) {
      out.push({ type: 'program', programId, meta: PROGRAM_META[programId] || { label: programId, hue: '#64748b', hue2: '#334155', emoji: '🏆' } });
    }
  });
  return out;
}

/**
 * Chapters that are in-progress AND close to done — for the nudge pop-up.
 * Only counts chapters the student has actually started (done > 0) and that
 * still have `threshold` or fewer items remaining. Empty/upcoming chapters
 * (total 0, or nothing started) are excluded.
 */
export function almostDoneChapters(subjectKey, allProgress, threshold = 3) {
  return (SUBJECT_CHAPTERS[subjectKey] || [])
    .map(ch => ({ chapterId: ch.id, title: chapterTitle(subjectKey, ch.id), ...chapterStats(subjectKey, ch.id, allProgress) }))
    .filter(c => !c.completed && c.total > 0 && c.done > 0 && c.remaining > 0 && c.remaining <= threshold);
}

/**
 * ALL in-progress chapters (started but not finished), sorted by fewest
 * remaining first — for the always-on nudge pop-up. Empty/upcoming chapters
 * (total 0, or nothing started) are excluded.
 */
export function inProgressChapters(subjectKey, allProgress) {
  return (SUBJECT_CHAPTERS[subjectKey] || [])
    .map(ch => ({ chapterId: ch.id, title: chapterTitle(subjectKey, ch.id), ...chapterStats(subjectKey, ch.id, allProgress) }))
    .filter(c => !c.completed && c.total > 0 && c.done > 0 && c.remaining > 0)
    .sort((a, b) => a.remaining - b.remaining);
}

/** Stable achievement key used for "already shown" persistence. */
export function achievementKey(a) {
  if (a.type === 'chapter') return `chapter:${a.subjectKey}:${a.chapterId}`;
  if (a.type === 'subject') return `subject:${a.subjectKey}`;
  return `program:${a.programId}`;
}
